import React from 'react'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { UnlinkControlsFromEvidenceRequest } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { RpcError } from 'grpc-web'
import isFunction from 'lodash/isFunction'
import log from 'loglevel'
import { useContext } from 'react'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { ConfirmationContext } from 'src/Confirmation'
import { getErrorMessage } from 'src/Utils/globalHelpers'
import { useConfirmationModal } from '../useConfirmationModal'

const UnlinkControlsBody = ({
  caption,
  controlNames,
}: {
  caption?: string
  controlNames: string[]
}): JSX.Element => {
  return (
    <div>
      <p>{`The following controls will be unlinked from '${caption}':`}</p>
      <ul>
        {controlNames.map((controlName, index) => (
          <li key={`${controlName}-${index}`}>{controlName}</li>
        ))}
      </ul>
    </div>
  )
}

export const useUnlinkControlsFromEvidence = ({
  evidenceId,
  controlIds,
  controlNames,
  caption,
  mutateFunc,
  title,
}: {
  evidenceId: string
  controlIds: string[]
  controlNames: string[]
  caption?: string
  mutateFunc?: () => Promise<void>
  title?: string
}): (() => void) => {
  const { setConfirmationState } = useContext(ConfirmationContext)
  const attachmentClient = useAuthorizedGrpcClient(AttachmentPromiseClient)

  const unlinkControlsFromEvidence = async () => {
    try {
      const unlinkControlsReq = new UnlinkControlsFromEvidenceRequest()
        .setEvidenceId(evidenceId)
        .setControlIdsList(controlIds)
      await attachmentClient.unlinkControlsFromEvidence(unlinkControlsReq)
    } catch (err: unknown) {
      const resError = err as RpcError
      const errorMessage = getErrorMessage(resError)
      if (errorMessage.shouldLog) {
        log.error(
          'Error in Unlink Controls from Evidence modal:',
          errorMessage.message,
        )
      }
      setConfirmationState({
        isShown: true,
        size: 'lg',
        title: 'Error Unlinking Controls',
        body: <p>{errorMessage.message}</p>,
        confirmText: 'OK',
        hideCancel: true,
        onConfirmCB: () => null,
      })
    } finally {
      isFunction(mutateFunc) && (await mutateFunc())
    }
  }
  const confirmationModalProps = {
    title: title || 'Are you sure you want to unlink the controls?',

    body: <UnlinkControlsBody caption={caption} controlNames={controlNames} />,
    confirmText: 'Unlink Controls',
    onConfirmCB: unlinkControlsFromEvidence,
  }

  return useConfirmationModal(confirmationModalProps)
}
